import { RingIncomingCallController } from '@infra/controllers/ring-incoming-call.controller';
import { UserLoginController } from '@infra/controllers/user-login.controller';
import { UserLogoutController } from '@infra/controllers/user-logout.controller';
import { WrapUpTimeEndedController } from '@infra/controllers/wrap-up-time-ended.controller';
import { WrapUpTimeStartedController } from '@infra/controllers/wrap-up-time-started.controller';

import { AcceptIncomingCallController } from '@infra/controllers/accept-incoming-call.controller';
import { ConnectedCallController } from '@infra/controllers/connected-call.controller';
import { DialNumberController } from '@infra/controllers/dial-number.controller';
import { DisconnectedCallController } from '@infra/controllers/disconnected-call.controller';
import { GetSignedRecordingUrlController } from '@infra/controllers/get-signed-recording-url.controller';
import { HangupCallController } from '@infra/controllers/hangup-call.controller';
import { HoldToggleController } from '@infra/controllers/hold-toggle.controller';
import { IdleCallController } from '@infra/controllers/idle-call.controller';
import { MuteToggleController } from '@infra/controllers/mute-toggle.controller';
import { RecordCallUuidController } from '@infra/controllers/record-call-uuid.controller';
import { RejectIncomingCallController } from '@infra/controllers/reject-incoming-call.controller';
import { SetOutboundNumberController } from '@infra/controllers/set-outbound-number.controller';
import { StatusSyncAircallToCrmController } from '@infra/controllers/status-sync-aircall-to-crm.controller';
import { StatusSyncCrmToAircallController } from '@infra/controllers/status-sync-crm-to-aircall.controller';
import { StoreIntegrationSettingsController } from '@infra/controllers/store-integration-settings.controller';
import { SyncIntegrationSettingUpdateController } from '@infra/controllers/sync-integration-setting-update.controller';
import { SyncParticipantsController } from '@infra/controllers/sync-participants.controller';
import { UsecaseFactory } from '@main/factories/usecase.factory';
import { SupervisorListenInController } from '../../call-monitor/supervisorListening/supervisor-listening.controller';
import { SupervisorDisconnectController } from '../../call-monitor/supervisorDisconnect/supervisor-disconnect.controller';
import { VoiceMailDropController } from '../../voiceMailDrop/voice-mail-drop.controller';
import { RecordingToggleController } from '../../call-recording/recording-toggle.controller';
import { BlindTransferNumberController } from '../../callTransfer/blindTransfer/blind-transfer-call.controller';
import { ConnectedTransferCallController } from '../../callTransfer/connect-merge-transfer/connect-merge-transfer-call.controller';
import { DisconnectedTransferCallController } from '../../callTransfer/disconnect-transfer/disconnect-transfer-call.controller';
import { HangupTransferCallController } from '../../callTransfer/hangup-transfer/hangup-transfer-call.controller';
import { MuteAgentController } from '../../callTransfer/MuteAgent/mute-agent.controller';
import { ShowAgentListController } from '../../callTransfer/showAgentList/show-agentlist.controller';
import { StoreAgentListController } from '../../callTransfer/storeAgentList/store-agentlist.controller';
import { SwapCallController } from '../../callTransfer/SwapCall/swap-call.controller';
import { SyncAgentStatusController } from '../../callTransfer/syncAgentStatus/sync-agent-status.controller';
import { TeammateAvailabilityChangedController } from '../../callTransfer/teammateAvailabilityChanged/teammate-availability-changed.controller';
import { WarmMergeTransferController } from '../../callTransfer/warmMergeTransfer/warm-merge-transfer.controller';
import { TransferNumberController } from '../../callTransfer/warmTransfer/warm-transfer-call.controller';
import { UserSSOLoginController } from '../../sso/controllers/user-sso-login.controller';
import { UserUnifiedLoginController } from '../../unifiedLogin/controllers/user-unified-login.controller';
import { FetchPrerecordedMessagesController } from '../../voiceMailDrop/fetch-list-prerecorded-messages.controller';

export class ControllerFactory {
  private static ringIncomingCallController: RingIncomingCallController;

  static buildRingIncomingCallController(): RingIncomingCallController {
    if (!this.ringIncomingCallController) {
      this.ringIncomingCallController = new RingIncomingCallController(
        UsecaseFactory.buildRingIncomingCallWithCrmOps(),
      );
    }

    return this.ringIncomingCallController;
  }
  private static wrapUpTimeStartedController: WrapUpTimeStartedController;
  static buildWrapUpTimeStartedController(): WrapUpTimeStartedController {
    return (
      this.wrapUpTimeStartedController ??
      (this.wrapUpTimeStartedController = new WrapUpTimeStartedController(
        UsecaseFactory.buildWrapUpTimeStartedControllerUseCase(),
      ))
    );
  }
  private static wrapUpTimeEndedController: WrapUpTimeEndedController;
  static buildWrapUpTimeEndedController(): WrapUpTimeEndedController {
    return (
      this.wrapUpTimeEndedController ??
      (this.wrapUpTimeEndedController = new WrapUpTimeEndedController(
        UsecaseFactory.buildWrapUpTimeEndedControllerUseCase(),
      ))
    );
  }

  private static userLoginController: UserLoginController;
  static buildUserLoginController(): UserLoginController {
    return (
      this.userLoginController ??
      (this.userLoginController = new UserLoginController(
        UsecaseFactory.buildUserLoginUsecase(),
      ))
    );
  }

  private static userSSOLoginController: UserSSOLoginController;
  static buildUserSSOLoginController(): UserSSOLoginController {
    return (
      this.userSSOLoginController ??
      (this.userSSOLoginController = new UserSSOLoginController(
        UsecaseFactory.buildUserSSOLoginUsecase(),
      ))
    );
  }

  private static userUnifiedLoginController: UserUnifiedLoginController;
  static buildUserUnifiedLoginController(): UserUnifiedLoginController {
    return (
      this.userUnifiedLoginController ??
      (this.userUnifiedLoginController = new UserUnifiedLoginController(
        UsecaseFactory.buildUserUnifiedLoginUsecase(),
      ))
    );
  }

  private static userLogoutController: UserLogoutController;
  static buildUserLogoutController(): UserLogoutController {
    return (
      this.userLogoutController ??
      (this.userLogoutController = new UserLogoutController(
        UsecaseFactory.buildUserLogoutUsecase(),
      ))
    );
  }

  private static acceptIncomingCallController: AcceptIncomingCallController;
  static buildAcceptIncomingCallController(): AcceptIncomingCallController {
    return (
      this.acceptIncomingCallController ??
      (this.acceptIncomingCallController = new AcceptIncomingCallController(
        UsecaseFactory.buildAcceptIncomingCallUsecase(),
      ))
    );
  }

  private static HangupCallController: HangupCallController;
  static buildHangupCallController(): HangupCallController {
    return (
      this.HangupCallController ??
      (this.HangupCallController = new HangupCallController(
        UsecaseFactory.buildHangupCallUsecase(),
      ))
    );
  }

  private static HangupTransferCallController: HangupTransferCallController;
  static buildHangupTransferCallController(): HangupTransferCallController {
    return (
      this.HangupTransferCallController ??
      (this.HangupTransferCallController = new HangupTransferCallController(
        UsecaseFactory.buildHangupTransferCallUsecase(),
      ))
    );
  }

  private static muteToggleController: MuteToggleController;
  static buildMuteToggleController(): MuteToggleController {
    return (
      this.muteToggleController ??
      (this.muteToggleController = new MuteToggleController(
        UsecaseFactory.buildMuteToggleUsecase(),
      ))
    );
  }

  private static recordingToggleController: RecordingToggleController;
  static buildRecordingToggleController(): RecordingToggleController {
    return (
      this.recordingToggleController ??
      (this.recordingToggleController = new RecordingToggleController(
        UsecaseFactory.buildRecordingToggleUsecase(),
      ))
    );
  }

  private static syncParticipantsController: SyncParticipantsController;
  static buildSyncParticipantsController(): SyncParticipantsController {
    return (
      this.syncParticipantsController ??
      (this.syncParticipantsController = new SyncParticipantsController(
        UsecaseFactory.buildSyncParticipantsUsecase(),
      ))
    );
  }

  private static holdToggleController: HoldToggleController;
  static buildHoldToggleController(): HoldToggleController {
    return (
      this.holdToggleController ??
      (this.holdToggleController = new HoldToggleController(
        UsecaseFactory.buildHoldToggleUsecase(),
      ))
    );
  }

  private static setOutboundNumberController: SetOutboundNumberController;
  static buildSetOutboundNumberController(): SetOutboundNumberController {
    return (
      this.setOutboundNumberController ??
      (this.setOutboundNumberController = new SetOutboundNumberController(
        UsecaseFactory.buildSetOutboundNumberUsecase(),
      ))
    );
  }

  private static dialNumberController: DialNumberController;
  static buildDialNumberController(): DialNumberController {
    return (
      this.dialNumberController ??
      (this.dialNumberController = new DialNumberController(
        UsecaseFactory.buildDialNumberUsecase(),
      ))
    );
  }

  private static recordCallUuidController: RecordCallUuidController;
  static buildRecordCallUuidController(): RecordCallUuidController {
    return (
      this.recordCallUuidController ??
      (this.recordCallUuidController = new RecordCallUuidController(
        UsecaseFactory.buildRecordCallUuidUsecase(),
      ))
    );
  }

  private static connectedCallController: ConnectedCallController;
  static buildConnectedCallController(): ConnectedCallController {
    return (
      this.connectedCallController ??
      (this.connectedCallController = new ConnectedCallController(
        UsecaseFactory.buildConnectedCallWithCrmOps(),
      ))
    );
  }

  private static disconnectedCallController: DisconnectedCallController;
  static buildDisconnectedCallController(): DisconnectedCallController {
    return (
      this.disconnectedCallController ??
      (this.disconnectedCallController = new DisconnectedCallController(
        UsecaseFactory.buildDisconnectedCallUsecase(),
      ))
    );
  }

  private static idleCallController: IdleCallController;
  static buildIdleCallController(): IdleCallController {
    return (
      this.idleCallController ??
      (this.idleCallController = new IdleCallController(
        UsecaseFactory.buildIdleCallUsecase(),
      ))
    );
  }

  private static rejectIncomingCallController: RejectIncomingCallController;
  static buildRejectIncomingCallController(): RejectIncomingCallController {
    return (
      this.rejectIncomingCallController ??
      (this.rejectIncomingCallController = new RejectIncomingCallController(
        UsecaseFactory.buildRejectIncomingCallUsecase(),
      ))
    );
  }

  private static statusSyncCrmToAircallController: StatusSyncCrmToAircallController;
  static buildStatusSyncCrmToAircallController(): StatusSyncCrmToAircallController {
    return (
      this.statusSyncCrmToAircallController ??
      (this.statusSyncCrmToAircallController =
        new StatusSyncCrmToAircallController(
          UsecaseFactory.buildStatusSyncCrmToAircallUsecase(),
        ))
    );
  }

  private static statusSyncAircallToCrmController: StatusSyncAircallToCrmController;
  static buildStatusSyncAircallToCrmController(): StatusSyncAircallToCrmController {
    return (
      this.statusSyncAircallToCrmController ??
      (this.statusSyncAircallToCrmController =
        new StatusSyncAircallToCrmController(
          UsecaseFactory.buildStatusSyncAircallToCrmUsecase(),
        ))
    );
  }

  private static StoreIntegrationSettingsController: StoreIntegrationSettingsController;
  static buildStoreIntegrationSettingsController(): StoreIntegrationSettingsController {
    return (
      this.StoreIntegrationSettingsController ??
      (this.StoreIntegrationSettingsController =
        new StoreIntegrationSettingsController(
          UsecaseFactory.buildStoreIntegrationSettingsUsecase(),
        ))
    );
  }

  private static transferNumberController: TransferNumberController;
  static buildTransferNumberController(): TransferNumberController {
    return (
      this.transferNumberController ??
      (this.transferNumberController = new TransferNumberController(
        UsecaseFactory.buildTransferNumberUsecase(),
      ))
    );
  }

  private static blindTransferNumberController: BlindTransferNumberController;
  static buildBlindTransferNumberController(): BlindTransferNumberController {
    return (
      this.blindTransferNumberController ??
      (this.blindTransferNumberController = new BlindTransferNumberController(
        UsecaseFactory.buildBlindTransferNumberUsecase(),
      ))
    );
  }

  private static storeAgentListController: StoreAgentListController;
  static buildStoreAgentListController(): StoreAgentListController {
    if (!this.storeAgentListController) {
      this.storeAgentListController = new StoreAgentListController(
        UsecaseFactory.buildStoreAgentListUsecase(),
      );
    }

    return this.storeAgentListController;
  }

  private static showAgentListController: ShowAgentListController;
  static buildShowAgentListController(): ShowAgentListController {
    if (!this.showAgentListController) {
      this.showAgentListController = new ShowAgentListController(
        UsecaseFactory.buildShowAgentListUsecase(),
      );
    }

    return this.showAgentListController;
  }

  private static teammateAvailabilityChangedController: TeammateAvailabilityChangedController;
  static buildTeammateAvailabilityChangedController(): TeammateAvailabilityChangedController {
    return (
      this.teammateAvailabilityChangedController ??
      (this.teammateAvailabilityChangedController =
        new TeammateAvailabilityChangedController(
          UsecaseFactory.buildTeammateAvailabilityChangedUsecase(),
        ))
    );
  }

  private static WarmTransferMergeController: WarmMergeTransferController;
  static buildWarmTransferMergeController(): WarmMergeTransferController {
    return (
      this.WarmTransferMergeController ??
      (this.WarmTransferMergeController = new WarmMergeTransferController(
        UsecaseFactory.buildWarmMergeTransferUsecase(),
      ))
    );
  }

  private static connectedTransferCallController: ConnectedTransferCallController;
  static buildConnectedTransferCallController(): ConnectedTransferCallController {
    return (
      this.connectedTransferCallController ??
      (this.connectedTransferCallController =
        new ConnectedTransferCallController(
          UsecaseFactory.buildConnectedTransferCallWithCrmOps(),
        ))
    );
  }

  private static disconnectedTransferCallController: DisconnectedTransferCallController;
  static buildDisconnectedTransferCallController(): DisconnectedTransferCallController {
    return (
      this.disconnectedTransferCallController ??
      (this.disconnectedTransferCallController =
        new DisconnectedTransferCallController(
          UsecaseFactory.buildDisconnectedTransferCallUsecase(),
        ))
    );
  }

  private static syncIntegrationSettingUpdateController: SyncIntegrationSettingUpdateController;
  static buildSyncIntegrationSettingUpdateController(): SyncIntegrationSettingUpdateController {
    return (
      this.syncIntegrationSettingUpdateController ??
      (this.syncIntegrationSettingUpdateController =
        new SyncIntegrationSettingUpdateController(
          UsecaseFactory.buildSyncIntegrationSettingUpdateUsecase(),
        ))
    );
  }

  private static syncAgentStatusController: SyncAgentStatusController;
  static buildSyncAgentStatusController(): SyncAgentStatusController {
    return (
      this.syncAgentStatusController ??
      (this.syncAgentStatusController = new SyncAgentStatusController(
        UsecaseFactory.buildSyncAgentStatusUsecase(),
      ))
    );
  }

  private static swapCallController: SwapCallController;
  static buildSwapCallController(): SwapCallController {
    return (
      this.swapCallController ??
      (this.swapCallController = new SwapCallController(
        UsecaseFactory.buildSwapCallUsecase(),
      ))
    );
  }

  private static getSignedRecordingUrlController: GetSignedRecordingUrlController;
  static buildGetSignedRecordingController(): GetSignedRecordingUrlController {
    return (
      this.getSignedRecordingUrlController ??
      (this.getSignedRecordingUrlController =
        new GetSignedRecordingUrlController(
          UsecaseFactory.buildGetSignedRecordingUrlUsecase(),
        ))
    );
  }
  private static muteAgenntController: MuteAgentController;
  static buildMuteAgentController(): MuteAgentController {
    return (
      this.muteAgenntController ??
      (this.muteAgenntController = new MuteAgentController(
        UsecaseFactory.buildMuteAgentCallUsecase(),
      ))
    );
  }
  private static supervisorListenInController: SupervisorListenInController;
  static buildSupervisorListenInController(): SupervisorListenInController {
    return (
      this.supervisorListenInController ??
      (this.supervisorListenInController = new SupervisorListenInController(
        UsecaseFactory.buildSupervisorListenUsecase(),
      ))
    );
  }
  private static supervisorDisconnectController: SupervisorDisconnectController;
  static buildSupervisorDisconnectController(): SupervisorDisconnectController {
    return (
      this.supervisorDisconnectController ??
      (this.supervisorDisconnectController = new SupervisorDisconnectController(
        UsecaseFactory.buildSupervisorDisconnectUsecase(),
      ))
    );
  }

  private static fetchPrerecordedMessagesController: FetchPrerecordedMessagesController;
  static buildFetchPrerecordedMessagesController(): FetchPrerecordedMessagesController {
    return (
      this.fetchPrerecordedMessagesController ??
      (this.fetchPrerecordedMessagesController =
        new FetchPrerecordedMessagesController(
          UsecaseFactory.buildFetchPrerecordedMessagesUsecase(),
        ))
    );
  }

  private static voiceMailDropController: VoiceMailDropController;
  static buildVoiceMailDropController(): VoiceMailDropController {
    return (
      this.voiceMailDropController ??
      (this.voiceMailDropController = new VoiceMailDropController(
        UsecaseFactory.buildVoiceMailDropUsecase(),
      ))
    );
  }
}
